import { 
  signInWithEmailLink, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  isSignInWithEmailLink
} from 'firebase/auth';
import { auth } from '../config/firebase';
import axios from 'axios';

// Get the API base URL from environment or use production URL
const API_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL;

/**
 * Send a passwordless sign-in link to the user's email using custom email template
 * @param {string} email - User's email address
 * @returns {Promise<Object>}
 */
export const sendSignInLink = async (email) => {
  try {
    // Get current path to redirect back to after authentication
    const currentPath = window.location.pathname;
    const returnUrl = currentPath.includes('/manage-donation/') ? currentPath : '/manage-donation';
    
    // Get donor info if available (for personalization)
    let firstName = '';
    try {
      const storedDonorInfo = window.localStorage.getItem('donorInfo');
      if (storedDonorInfo) {
        const donorInfo = JSON.parse(storedDonorInfo);
        firstName = donorInfo.firstName || '';
      }
    } catch (e) {
      // Ignore parsing errors
    }

    // Call our backend endpoint that uses Firebase Admin SDK to generate the link
    // and sends it with our custom email template
    const response = await axios.post(`${API_BASE_URL}/sendPasswordlessSignInLink`, {
      email,
      returnUrl,
      firstName,
    });

    if (response.data.success) {
      // Save the email locally so we can use it when the user clicks the link
      window.localStorage.setItem('emailForSignIn', email);
      return { success: true };
    } else {
      return { success: false, error: response.data.error || 'Failed to send sign-in link' };
    }
  } catch (error) {
    console.error('Error sending sign-in link:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || 'Failed to send sign-in link'
    };
  }
};

/**
 * Complete the sign-in process with the email link
 * @param {string} email - User's email address
 * @param {string} emailLink - The sign-in link from email
 * @returns {Promise<Object>}
 */
export const confirmSignInWithEmailLink = async (email, emailLink) => {
  try {
    if (isSignInWithEmailLink(auth, emailLink)) {
      // Complete the sign-in process
      const result = await signInWithEmailLink(auth, email, emailLink);
      
      // Clear the email from storage
      window.localStorage.removeItem('emailForSignIn');
      
      return { success: true, user: result.user };
    } else {
      return { success: false, error: 'Invalid sign-in link' };
    }
  } catch (error) {
    console.error('Error confirming sign-in:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if the current URL is a sign-in link
 * @returns {boolean}
 */
export const isValidSignInLink = () => {
  return isSignInWithEmailLink(auth, window.location.href);
};

/**
 * Get the stored email for sign-in
 * @returns {string|null}
 */
export const getStoredEmailForSignIn = () => {
  return window.localStorage.getItem('emailForSignIn');
};

/**
 * Sign out the current user
 * @returns {Promise<Object>}
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Set up authentication state listener
 * @param {Function} callback - Callback function to handle auth state changes
 * @returns {Function} - Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current user
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};